import React, { useState, useMemo } from "react";
import { LayoutModalWorkerItem } from "../layout/LayoutModalWorkerItem";
import { WorkItem } from "services/input-core/work-items/@types";
import { TFunction, useTranslation } from "react-i18next";
import {
  Box,
  Tab,
  Tabs,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ExtractTypeEnum, WorkItemResultEnum } from "utils/@types";
import Badge from "components/UI/Badge/Badge";
import { StyledParagraph } from "./styles";
import Table from "components/Tables/Table/Table.js";
import { renderAnalysisIcon } from "utils/functions";
import { useActivityItemContext } from "store/activityItem";
import { Download } from "@mui/icons-material";

const tableHeaders = ["field", "expected", "extracted", "status"];

const renderAnalysisStatus = (status: any, t: any) => {
  const renderedStatus = (status: any, color: any) => (
    <Badge color={color}>
      {t(`modal-table-status-${status}`, { ns: "status" })}
    </Badge>
  );

  switch (status) {
    case WorkItemResultEnum.HOMOLOGADO:
      return renderedStatus("valid", "success");
    case WorkItemResultEnum.HOMOLOGATED:
      return renderedStatus("valid", "success");
    case WorkItemResultEnum.MANUALLY_HOMOLOGATED:
      return renderedStatus("valid", "success");
    case WorkItemResultEnum.Approved:
      return renderedStatus("valid", "success");
    case WorkItemResultEnum.NAO_HOMOLOGADO:
      return renderedStatus("invalid", "danger");
    case WorkItemResultEnum.Not_Approved:
      return renderedStatus("invalid", "danger");
    default:
      return renderedStatus("error", "warning");
  }
};

const renderAnalysisExtracted = (
  extract: string,
  t: TFunction<"analysis-modal", undefined>,
  fieldName: string
) => {
  const extractTranslations: Record<string, string> = {
    [ExtractTypeEnum.INVALIDO]: "modal-table-extract-invalid-doc",
    [ExtractTypeEnum.CAMPO_NAO_ENCONTRADO]: "modal-table-extract-invalid-field",
    [ExtractTypeEnum.DOCUMENTO_INVALIDO]:
      "modal-table-extract-invalid-document",
    [ExtractTypeEnum.TIPO_DOCUMENTO]: "modal-table-extract-document-type",
    [ExtractTypeEnum.RULE_FIELD_NOT_FOUND]:
      "modal-table-extract-field-not-found",
    [ExtractTypeEnum.PREENCHIDO]: "modal-table-extract-filled",
    [ExtractTypeEnum.NAO_PREENCHIDO]: "modal-table-extract-field-not-filled",
    [ExtractTypeEnum.DOCUMENTO_PREENCHIDO]:
      "modal-table-extract-value-completed-document",
    [ExtractTypeEnum.INVALID_DOCUMENT]: "modal-table-extract-invalid-doc",
    [ExtractTypeEnum.ATTACHMENTS_COUNT]:
      "modal-table-extract-attachments-count",
    [ExtractTypeEnum.INVALID_DATE]: "modal-table-extract-invalid-date",
    [ExtractTypeEnum.FIELDGLASS_UNAVAILABLE]:
      "modal-table-fieldglass-unavailable",
  };

  if (extract in extractTranslations) {
    return t(extractTranslations[extract]);
  }

  if (fieldName === "contractsNumber" && extract) {
    try {
      return Array.isArray(JSON.parse(extract))
        ? JSON.parse(extract).join(" - ")
        : extract.split("/").join(" - ");
    } catch (_) {
      return extract.split("/").join(" - ");
    }
  }

  return extract;
};

interface NewModalAnalysisProps {
  modalOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
}

export const NewModalAnalysis: React.FC<NewModalAnalysisProps> = ({
  modalOpen,
  onClose,
  title,
  subtitle,
}) => {
  const { t } = useTranslation(["analysis-modal", "list-messages", "modals"]);

  const { currentActivityItem } = useActivityItemContext((state) => ({
    currentActivityItem: state.currentActivity,
  }));

  const [processTab, setProcessTab] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setProcessTab(newValue);
  };

  const analysis: any[] = useMemo(() => {
    return (
      currentActivityItem?.workitems?.map((items: WorkItem) => {
        return items.analyzes;
      }) ?? []
    );
  }, [currentActivityItem]);

  const analyzedContent =
    analysis[processTab]
      ?.filter((item: any) => item.fieldCompared)
      ?.map((analysis: any) => [
        t(`modal-table-cell-${analysis.fieldName}`),
        renderAnalysisExtracted(analysis.fieldCompared, t, analysis.fieldName),
        renderAnalysisExtracted(analysis.fieldExtracted, t, analysis.fieldName),
        renderAnalysisStatus(analysis.status, t),
      ]) || [];

  const tableHeadersFormatted = tableHeaders.map((header) =>
    t(`modal-table-header-${header}`)
  );

  return (
    <LayoutModalWorkerItem
      modalOpen={modalOpen}
      onClose={onClose}
      title={title ?? t("modal-title")}
      subtitle={subtitle ?? currentActivityItem?.activityItemAction}
    >
      {currentActivityItem.workitems.length > 0 && (
        <Box sx={{ width: "100%", borderBottom: 2, borderColor: "divider" }}>
          <Tabs
            value={processTab}
            onChange={handleChange}
            textColor="secondary"
            indicatorColor="secondary"
            variant="scrollable"
            scrollButtons="auto"
            aria-label="tabs process"
          >
            {currentActivityItem.workitems.map((item: any, index: number) => (
              <Tab
                label={
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 5 }}
                  >
                    {`${t("processing", { ns: "modals" })} ${index + 1}`}
                    {renderAnalysisIcon(item.result)}
                  </span>
                }
                disabled={item.analyzes.length <= 0}
                key={item?._id}
              />
            ))}
          </Tabs>
        </Box>
      )}

      <Box
        sx={{
          marginTop: "0.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {currentActivityItem.workitems[processTab]?.processedAt && (
          <Box>
            <span>
              {t("processedAt", { ns: "modals" })}:{" "}
              {new Intl.DateTimeFormat("pt-BR", {
                timeZone: "America/Sao_Paulo",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              }).format(
                new Date(currentActivityItem.workitems[processTab].processedAt)
              )}
            </span>
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {currentActivityItem.workitems[processTab]?.documents?.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                borderRadius: "12px",
                padding: "1rem",
              }}
            >
              <Select
                fullWidth
                displayEmpty
                onChange={(e) => {
                  const selectedDocument = currentActivityItem.workitems[
                    processTab
                  ]?.documents?.find((doc: any) => doc.name === e.target.value);

                  if (selectedDocument?.url) {
                    window.open(selectedDocument.url, "_blank");
                  } else {
                    console.warn(
                      `URL não encontrada para o documento: ${e.target.value}`
                    );
                  }
                }}
                renderValue={() => (
                  <Tooltip title={t("download", { ns: "modals" })}>
                    <IconButton
                      sx={{
                        width: "40px",
                        height: "40px",
                        padding: "8px",
                        borderRadius: "50%",
                      }}
                    >
                      <Download />
                    </IconButton>
                  </Tooltip>
                )}
                sx={{
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  appearance: "none",
                  "& .MuiSelect-select": {
                    paddingRight: 0,
                  },
                  "& .MuiSelect-icon": {
                    display: "none",
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "4px !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: "12px",
                  },
                  "& .MuiSelect-outlined.MuiSelect-outlined": {
                    padding: "0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3f51b5",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3f51b5",
                  },
                }}
              >
                {currentActivityItem.workitems[processTab].documents.map(
                  (doc: any, index: number) => (
                    <MenuItem key={doc.name} value={doc.name}>
                      {`${"Document " + (index + 1)} - ${doc.type}`}
                    </MenuItem>
                  )
                )}
              </Select>
            </Box>
          )}
        </Box>
      </Box>

      <Box>
        {analysis[processTab]?.length === 1 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            marginTop="1rem"
            width="100%"
          >
            <StyledParagraph>
              {renderAnalysisExtracted(
                analysis[processTab][0]?.fieldExtracted,
                t,
                ""
              )}
            </StyledParagraph>
          </Box>
        ) : (
          <Table
            hover
            tableHeaderColor="success"
            tableHead={tableHeadersFormatted}
            tableData={analyzedContent}
          />
        )}
      </Box>
    </LayoutModalWorkerItem>
  );
};
